import React, { useState, useEffect } from 'react';

import styled, { css } from 'styled-components';

import * as MUI from '@mui/material';
import * as MUIIcon from '@mui/icons-material';
import { Theme } from '../Theme';
import { FlexColumn } from '../Column';
import { useRef } from 'react';
import { Button } from '../Button';
import { FlexRow } from '../Row';

const InputColumn = styled(FlexColumn)`
	margin: 0.5rem 0;
	flex: 1;
	flex-basis: calc(50% - 1rem);
	align-self: flex-start;
`;

export const InputLabel = styled.label`
	width: auto;
	min-height: 18px;
	padding-left: 10px;
	color: ${Theme.black};
	font-size: 12px;
	line-height: 16px;
	text-align: left;
`;

export const InputRequired = styled.span`
	padding-left: 4px;
	color: ${Theme.error};
	font-size: 16px;
	line-height: 16px;
	vertical-align: sub;
`;

export const InputMessage = styled.span`
	width: 100%;
	min-height: 18px;
	padding-left: 10px;
	color: ${Theme.darkGrey};
	font-size: 12px;
	line-height: 16px;
	text-align: left;
`;

export const InputError = styled.span`
	width: 100%;
	min-height: 18px;
	padding-left: 10px;
	color: ${Theme.error};
	font-size: 12px;
	line-height: 16px;
	text-align: left;
`;

export const InputLength = styled.span`
	padding: 0 4px;
	margin-left: auto;
	color: ${(props) => (props.error ? Theme.error : Theme.blue)};
	font-size: 12px;
	line-height: 16px;
`;

export const InfoIcon = styled(MUIIcon.Info)`
	font-size: 16px !important;
	color: ${Theme.mediumGrey} !important;
`;

export const InputGroup = ({ label = '', markRequired = false, message = '', inputLength = 0, maxLength = 0, info = '', error = '', style = {}, ...props }) => {
	return (
		<InputColumn style={{ ...style }}>
			<div style={{ display: 'flex' }}>
				{label ? (
					<InputLabel>
						{label}
						{markRequired ? <InputRequired>*</InputRequired> : null}
					</InputLabel>
				) : null}

				{maxLength ? (
					<InputLength error={error}>
						{inputLength}/{maxLength}
					</InputLength>
				) : null}

				{info && (
					<MUI.Tooltip title={info}>
						<InfoIcon />
					</MUI.Tooltip>
				)}
			</div>

			{props.children}

			{message ? <InputMessage>{message}</InputMessage> : null}

			{error ? <InputError>{error}</InputError> : null}
		</InputColumn>
	);
};

const CommonInputStyle = css`
	width: auto;
	height: 2rem;
	padding: 0 !important;
	margin: 0.25rem 0 !important;

	& .MuiInputBase-root,
	.MuiOutlinedInput-root {
		padding: 0 !important;
	}

	& .MuiInputBase-input {
		padding: 0.35rem 0.4rem !important;
		color: ${(props) => (props.disabled ? Theme.darkGrey : Theme.black)};
		background-color: ${Theme.white} !important;
		font-family: inherit;
		font-size: 14px;
		cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')} !important;

		::placeholder,
		::-webkit-input-placeholder {
			font-family: inherit !important;
			font-size: 12px !important;
			color: ${(props) => (props.disabled ? Theme.mediumGrey : Theme.black)} !important;
		}
	}

	& .MuiOutlinedInput-notchedOutline {
		border-color: ${(props) => (props.error ? Theme.red : null)} !important;
		color: ${(props) => (props.error ? Theme.red : null)} !important;
	}
`;

const CommonInputAttributes = (props) => {
	const attributes = {
		type: props.type || '',
		label: props.label || '',
		variant: props.variant || 'outlined',
		color: props.color || 'secondary',
		disabled: props.disabled || false,
	};

	if (props.primary || props.secondary || props.success || props.error) {
		if (props.primary) {
			attributes.color = 'primary';
		} else if (props.secondary) {
			attributes.color = 'secondary';
		} else if (props.success) {
			attributes.color = 'success';
		} else if (props.error) {
			attributes.color = 'error';
		}
	}

	return attributes;
};

export const AdditionalInputProps = [
	'primary',
	'secondary',
	'success',
	'error',
	'fullWidth',
	'alignLeft',
	'alignCenter',
	'alignRight',
	'textLeft',
	'textRight',
	'textCenter',
	'loading',
	'outline',
	'disabled',
];
// .withConfig({
//     shouldForwardProp: (prop) => !(AdditionalInputProps.includes(prop)),
// })
export const Input = styled(MUI.OutlinedInput).attrs((props) => ({
	...CommonInputAttributes({
		...props,
	}),
}))`
	${CommonInputStyle}
`;

export const Password = styled(MUI.OutlinedInput).attrs((props) => ({
	...CommonInputAttributes({
		type: 'password',
		...props,
	}),
}))`
	${CommonInputStyle}
`;

export const TextField = styled(MUI.OutlinedInput).attrs((props) => ({
	...CommonInputAttributes({
		type: 'text',
		...props,
	}),
}))`
	${CommonInputStyle}
`;

export const Number = styled(MUI.OutlinedInput).attrs((props) => ({
	...CommonInputAttributes({
		type: 'number',
		...props,
	}),
}))`
	${CommonInputStyle}
`;

export const TextArea = styled(MUI.OutlinedInput).attrs((props) => ({
	...CommonInputAttributes({
		type: 'text',
		...props,
	}),
	multiline: true,
	rows: 4,
}))`
	${CommonInputStyle}
	height: auto;
`;

export const MenuItem = styled(MUI.MenuItem)`
	margin: 0 0.5rem !important;
	background-color: ${Theme.transparent} !important;
	line-height: 24px !important;
	font-size: 12px !important;
	text-transform: uppercase !important;
	border: 0 solid ${Theme.lightGrey} !important;
	border-bottom-width: 1px !important;

	&:after {
		display: ${(props) => (!props.disabled && props.selected ? 'block' : 'none')};
		content: 'Selected';
		margin-left: auto;
		font-size: 10px !important;
		color: ${Theme.blue};
	}
`;

export const SelectTitle = styled(MenuItem).attrs((props) => ({
	disabled: true,
	value: '',
}))`
	margin: 0 !important;
	padding-left: 1.6rem !important;
	border-width: 0px !important;
	color: ${Theme.black} !important;
	font-weight: 500 !important;
	opacity: 1 !important;
`;

export const Select = styled(MUI.Select).attrs((props) => ({
	...CommonInputAttributes({
		...props,
	}),
	renderValue: props.value === '' ? () => <span style={{ color: Theme.darkGrey, opacity: '0.75', fontSize: '12px' }}>{props.placeholder}</span> : null,
	displayEmpty: true,
}))`
	${CommonInputStyle}
`;

const RadioContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	flex-direction: ${(props) => (props.fullWidth ? 'column' : '')};
	row-gap: 0.5rem;
	column-gap: 0.5rem;
	width: 100%;
	margin: 0.24rem 0;
	font-size: 14px;
	justify-content: space-between;
`;

const RadioItem = styled.label`
	display: flex;
	width: inherit;
	flex-basis: ${(props) => (props.fullWidth ? 'auto' : 'calc(50% - 0.5rem')});
	min-height: 1.93rem;
	margin: 0;
	color: ${(props) => (props.disabled ? Theme.lightGrey : Theme.black)};
	background-color: ${Theme.white};
	border: 1px solid ${(props) => (props.error ? Theme.error : Theme.mediumGrey)};
	border-radius: 4px;
	align-items: center;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const Radio = styled.input.attrs({ type: 'radio' })`
	margin: 0 8px 0px 8px;
`;

const RadioColumn = styled(FlexColumn)`
	gap: 0.25rem;
	padding: 0.5rem 0;
`;

const RadioLabel = styled(FlexRow)``;

const RadioDescription = styled(FlexRow)`
	font-size: 12px;
	color: ${Theme.darkGrey};
`;

export const RadioGroup = ({ name = '', fullWidth = false, placeholder = '', value = '', disabled = false, options = [], error = '', onClick = null, ...props }) => {
	return (
		<RadioContainer fullWidth={fullWidth}>
			{options.map((option, index) => (
				<RadioItem
					fullWidth={fullWidth}
					error={error}
					disabled={disabled || option.disabled}
				>
					<Radio
						key={index}
						name={name}
						checked={value === option.value}
						disabled={disabled || option.disabled}
						value={option.value}
						onClick={(e) => (onClick ? onClick(e) : null)}
					/>
					<RadioColumn>
						<RadioLabel>{option.name}</RadioLabel>
						{option.description && <RadioDescription>{option.description}</RadioDescription>}
					</RadioColumn>
				</RadioItem>
			))}

			{error ? <InputError>{error}</InputError> : null}
		</RadioContainer>
	);
};

export const Chip = styled(MUI.Chip)``;

export const InputAdornment = styled(MUI.InputAdornment)``;

export const CheckboxContainer = styled.div`
	display: flex;
	margin: 0.24rem 0;
	flex-wrap: wrap;
	row-gap: 0.5rem;
	column-gap: 0.5rem;
	width: 100%;
	font-size: 14px;
	justify-content: space-between;
`;

export const CheckboxItem = styled.label`
	display: flex;
	flex-basis: calc(50% - 0.5rem);
	height: 1.93rem;
	margin: 0;
	color: ${(props) => (props.disabled ? Theme.lightGrey : Theme.black)};
	background-color: ${Theme.white};
	border: 1px solid ${(props) => (props.error ? Theme.error : Theme.mediumGrey)};
	border-radius: 4px;
	align-items: center;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
	margin: 0 8px 0 8px;
`;

export const CheckboxGroup = ({ name = '', placeholder = '', value = '', loading = false, disabled = false, options = [], error = '', onClick = null, ...props }) => {
	const [selectedOptions, setSelectedOptions] = useState([]);

	const handleOptionSelect = (e) => {
		let newSelectedOptions = selectedOptions;

		if (newSelectedOptions.includes(e.target.value)) {
			newSelectedOptions = newSelectedOptions.filter((field) => field !== e.target.value);
		} else {
			newSelectedOptions = [...newSelectedOptions, e.target.value];
		}

		setSelectedOptions(newSelectedOptions);
		if (onClick) {
			e.target.value = newSelectedOptions.length ? newSelectedOptions.join(',') : '';
			onClick(e);
		}
	};

	useEffect(() => {
		setSelectedOptions(value || []);
	}, [value]);

	return (
		<CheckboxContainer>
			{options.map((option, index) => (
				<CheckboxItem>
					<Checkbox
						key={index}
						name={name}
						checked={selectedOptions.includes(option.value)}
						disabled={disabled || option.disabled}
						value={option.value}
						onClick={(e) => {
							handleOptionSelect(e);
						}}
					/>
					{option.name}
				</CheckboxItem>
			))}
		</CheckboxContainer>
	);
};

export const FileUpload = ({ label = 'Upload', multiple = true, disabled = false, accept = 'image/*', onInput = null, onChange = null, ...props }) => {
	const uploadInputRef = useRef(null);

	return (
		<>
			<input
				ref={uploadInputRef}
				type='file'
				accept={accept}
				multiple={multiple}
				disabled={disabled}
				style={{ display: 'none' }}
				onInput={onInput}
				onChange={onChange}
			/>
			<Button
				disabled={disabled}
				onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
			>
				{label}
			</Button>
		</>
	);
};
